import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { communicationThunks, communicationActions } from 'src/store/slices/communicationSlice';
import * as signalR from '@microsoft/signalr';
import { getUser, getToken } from 'src/utils/Common';

const ThuliumContext = createContext(null);

export function useThulium() {
  const context = useContext(ThuliumContext);
  if (!context) {
    throw new Error('useThulium must be used within ThuliumProvider');
  }
  return context;
}

export function ThuliumProvider({ children, gatewayUrl }) {
  const dispatch = useDispatch();
  const SessionUser = getUser();
  const currentPhoneLogId = useSelector((state) => state.communication.currentPhoneLogId);

  const [isInitialized, setIsInitialized] = useState(false);
  const [activeContact, setActiveContact] = useState(null);
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [callLogs] = useState([]);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  const activeContactRef = useRef(null);
  const currentPhoneLogIdRef = useRef(null);
  const activeCustomerRef = useRef(null);
  const connectionRef = useRef(null);
  const retryTimerRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const processedEndEventsRef = useRef(new Map());
  const processedEndedCallIdsRef = useRef(new Set());

  // Handler refs - prevent dependency loop: handler changes don't trigger reconnect
  const handleContactAcceptedRef = useRef(null);
  const handleContactEndedRef = useRef(null);

  useEffect(() => { activeContactRef.current = activeContact; }, [activeContact]);
  useEffect(() => { currentPhoneLogIdRef.current = currentPhoneLogId; }, [currentPhoneLogId]);
  useEffect(() => { activeCustomerRef.current = activeCustomer; }, [activeCustomer]);
  useEffect(() => { handleContactAcceptedRef.current = handleContactAccepted; }, [handleContactAccepted]);
  useEffect(() => { handleContactEndedRef.current = handleContactEnded; }, [handleContactEnded]);

  const normalizeCallData = useCallback((data) => {
    const payload = data || {};
    return {
      callId: payload.callId || null,
      phone: payload.phoneNumber || null,
      callStartTime: payload.callStartTime || null,
      callEndTime: payload.callEndTime || null,
      callStatus: payload.callStatus || null,
      duration: payload.duration || null,
      agentId: payload.agentId || null,
      employeeId: payload.employeeId || null,
      callSessionId: payload.callSessionId || null,
      raw: payload
    };
  }, []);

  const handleContactAccepted = useCallback(async (incomingCallData) => {
    const normalized = normalizeCallData(incomingCallData);
    const contactId = normalized.callId;

    if (contactId && activeContactRef.current?.contactId === contactId) {
      return;
    }

    // Clear stale state from previous call before processing new one
    dispatch(communicationActions.clearIncomingPhoneNumber());
    dispatch(communicationActions.clearActiveCustomer());

    const phone = normalized.phone;
    const startTime = new Date(normalized.callStartTime || new Date());

    if (!phone) {
      console.warn('[Thulium] CallAccepted: no phone number in payload');
      const contactData = {
        contactId: contactId || `unknown-${Date.now()}`,
        phone: 'Unknown',
        startTime,
        data: normalized.raw
      };
      setActiveContact(contactData);
      activeContactRef.current = contactData;
      setActiveCustomer(null);
      activeCustomerRef.current = null;
      dispatch(communicationActions.clearActiveCustomer());
      return;
    }

    const contactData = {
      contactId: contactId || `unknown-${Date.now()}`,
      phone,
      startTime,
      data: normalized.raw
    };
    setActiveContact(contactData);
    activeContactRef.current = contactData;

    try {
      const result = await dispatch(communicationThunks.GetConsumerByPhoneNumber(phone)).unwrap();

      if (result && result.isSuccess) {
        const customer = {
          id: result.Id,
          firstName: result.FirstName,
          lastName: result.LastName,
          name: `${result.FirstName} ${result.LastName}`,
          phone
        };
        setActiveCustomer(customer);
        activeCustomerRef.current = customer;
        dispatch(communicationActions.setActiveCustomer(customer));
      } else {
        setActiveCustomer(null);
        activeCustomerRef.current = null;
        dispatch(communicationActions.clearActiveCustomer());
      }

      const phoneLogDto = {
        App: 'CallCenter',
        CallingPhone: phone,
        CreateDate: startTime.toISOString(),
        EmployeeId: SessionUser.TechnicianId,
        AgentId: normalized.agentId || null,
        CallSessionId: normalized.callSessionId || null,
        RequestPayload: JSON.stringify(normalized.raw),
      };

      const logResult = await dispatch(communicationThunks.AddPhoneLog(phoneLogDto)).unwrap();

      // API returns plain int (entity.Id), not an object
      const logId = typeof logResult === 'number'
        ? logResult
        : (logResult?.id || logResult?.Id || logResult?.logId || logResult?.LogId);

      if (logId) {
        currentPhoneLogIdRef.current = logId;
      } else {
        console.error('[Thulium] AddPhoneLog response missing ID:', logResult);
      }

    } catch (err) {
      console.error('[Thulium] handleContactAccepted error:', err?.message);
      setActiveCustomer(null);
      activeCustomerRef.current = null;
      dispatch(communicationActions.clearActiveCustomer());
    }
  }, [dispatch, normalizeCallData, currentPhoneLogId]);

  const handleContactEnded = useCallback(async (endedCallData) => {
    const normalized = normalizeCallData(endedCallData);
    const contactId = normalized.callId;
    const dedupeKey = `${contactId || 'unknown'}:${normalized.callStatus || 'ended'}`;
    const lastProcessedAt = processedEndEventsRef.current.get(dedupeKey);
    const now = Date.now();

    if (lastProcessedAt && now - lastProcessedAt < 5000) {
      return;
    }
    processedEndEventsRef.current.set(dedupeKey, now);
    if (contactId) {
      processedEndedCallIdsRef.current.add(contactId);
    }

    const logId = currentPhoneLogIdRef.current || currentPhoneLogId;

    if (!activeContactRef.current && !logId) {
      return;
    }

    if (logId) {
      try {
        await dispatch(communicationThunks.UpdatePhoneLog(logId)).unwrap();
        dispatch(communicationActions.clearCurrentPhoneLogId());
        currentPhoneLogIdRef.current = null;
      } catch (err) {
        console.error('[Thulium] UpdatePhoneLog error:', err?.message);
      }
    } else {
      console.error('[Thulium] CallEnded: no phone log ID found, UpdatePhoneLog skipped');
    }

    setActiveContact(null);
    activeContactRef.current = null;
    setActiveCustomer(null);
    activeCustomerRef.current = null;
    dispatch(communicationActions.clearActiveCustomer());
    dispatch(communicationActions.clearIncomingPhoneNumber());
  }, [dispatch, normalizeCallData, currentPhoneLogId]);

  const setupSignalRListeners = useCallback((connection) => {
    connection.on('CallAccepted', (callData) => {
      const normalized = normalizeCallData(callData);
      if (!normalized.callId) return;
      if (processedEndedCallIdsRef.current.has(normalized.callId)) return;
      if (handleContactAcceptedRef.current) handleContactAcceptedRef.current(callData);
    });

    connection.on('CallEnded', (callData) => {
      const normalized = normalizeCallData(callData);
      if (!normalized.callId) return;
      if (processedEndedCallIdsRef.current.has(normalized.callId)) return;
      if (handleContactEndedRef.current) handleContactEndedRef.current(callData);
    });

    connection.on('PhoneCallCreated', (callData) => {
      const normalized = normalizeCallData(callData);
      if (!normalized.callId) return;
      if (normalized.callStatus === 'Ended') {
        if (handleContactEndedRef.current) handleContactEndedRef.current(callData);
      } else {
        if (handleContactAcceptedRef.current) handleContactAcceptedRef.current(callData);
      }
    });

    connection.on('HeartbeatAck', () => {
      // server alive
    });

    connection.on('CallError', (errorData) => {
      const message = errorData?.ErrorMessage || errorData?.errorMessage || 'Unknown call error';
      console.error('[Thulium] CallError:', message);
      setError(`Call error: ${message}`);
    });

    connection.on('ConnectionStatus', (statusData) => {
      console.log('[Thulium] ConnectionStatus:', statusData.Status, statusData.Message);
    });
  }, [normalizeCallData]);

  useEffect(() => {
    const initializeSignalR = async () => {
      if (connectionRef.current && connectionRef.current.state !== signalR.HubConnectionState.Disconnected) {
        return;
      }

      if (!gatewayUrl) {
        console.error('[Thulium] Gateway URL not provided');
        setError('Gateway URL not provided');
        return;
      }

      try {
        const authToken = getToken();
        const employeeId = Number(SessionUser?.TechnicianId);
        const baseUrl = (gatewayUrl || '').replace(/\/$/, '');
        const normalizedHubUrl = /\/hubs?\//i.test(baseUrl) || /phoneCallHub$/i.test(baseUrl)
          ? baseUrl
          : `${baseUrl}/phoneCallHub`;

        const connection = new signalR.HubConnectionBuilder()
          .withUrl(normalizedHubUrl, {
            withCredentials: true,
            accessTokenFactory: () => authToken || ''
          })
          .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
          .withHubProtocol(new signalR.JsonHubProtocol())
          .configureLogging(signalR.LogLevel.Warning)
          .build();

        connection.onreconnecting(() => {
          setConnectionStatus('connecting');
        });

        connection.onreconnected((connectionId) => {
          console.log('[Thulium] Reconnected:', connectionId);
          setConnectionStatus('connected');
          setIsInitialized(true);
          if (Number.isFinite(employeeId) && employeeId > 0) {
            connection.invoke('JoinEmployeeGroup', employeeId).catch((e) => {
              console.warn('[Thulium] Re-join group failed:', e?.message);
            });
          }
        });

        connection.onclose((error) => {
          if (error) console.warn('[Thulium] Connection closed with error:', error?.message);
          setConnectionStatus('disconnected');
          setIsInitialized(false);
          if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
          }
        });

        setupSignalRListeners(connection);

        await connection.start();
        console.log('[Thulium] Connected to PhoneCallHub');

        if (Number.isFinite(employeeId) && employeeId > 0) {
          try {
            await connection.invoke('JoinEmployeeGroup', employeeId);
          } catch (groupJoinError) {
            console.warn('[Thulium] JoinEmployeeGroup failed:', groupJoinError?.message);
          }
        } else {
          console.warn('[Thulium] TechnicianId invalid, employee group join skipped');
        }

        setConnectionStatus('connected');
        setIsInitialized(true);
        connectionRef.current = connection;

        // Heartbeat: ping server every 30s to detect silent disconnects
        heartbeatIntervalRef.current = setInterval(async () => {
          if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
            try {
              await connectionRef.current.invoke('Heartbeat');
              console.log('[Thulium] Heartbeat OK');
            } catch (heartbeatErr) {
              console.warn('[Thulium] Heartbeat failed:', heartbeatErr?.message);
              setConnectionStatus('disconnected');
              setIsInitialized(false);
            }
          }
        }, 30000);

      } catch (err) {
        console.error('[Thulium] SignalR init failed:', err?.message);
        setError(`Failed to initialize SignalR: ${err.message}`);
        setConnectionStatus('disconnected');

        retryTimerRef.current = setTimeout(() => {
          initializeSignalR();
        }, 5000);
      }
    };

    if (gatewayUrl) {
      initializeSignalR();
    }

    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (connectionRef.current) {
        connectionRef.current.stop().catch(() => {});
        connectionRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayUrl]); // setupSignalRListeners excluded: uses handler refs, won't cause reconnect

  const value = {
    isInitialized,
    activeContact,
    activeCustomer,
    callLogs,
    error,
    connectionStatus,
    connection: connectionRef.current
  };

  return (
    <ThuliumContext.Provider value={value}>
      {children}
    </ThuliumContext.Provider>
  );
}

export default ThuliumProvider;
